import React, {useContext} from 'react';
import {observer} from 'mobx-react';
import {CheckmarkFilled, ErrorFilled} from '@carbon/icons-react';
import {NetworkContext} from '../lib/context';
import './upload-status-chip.scss';

type Props = {
    onOpen: () => void;
};

const UploadStatusChip = observer(({onOpen}: Props) => {
    const store = useContext(NetworkContext);
    const jobs = store.uploadJobs;
    if (jobs.length === 0) return null;

    const activeCount = store.activeUploadCount;
    const doneCount = jobs.filter(j => j.phase === 'done').length;
    const errorCount = jobs.filter(j => j.phase === 'error' || j.phase === 'interrupted').length;
    const isActive = activeCount > 0;
    const hasErrors = errorCount > 0;

    let chipModifier = '';
    let icon: React.ReactNode;
    let label: string;

    if (isActive) {
        icon = <span className="upload-status-chip__spinner" aria-hidden="true" />;
        label = `Uploading ${activeCount} file${activeCount !== 1 ? 's' : ''}…`;
    } else if (hasErrors) {
        chipModifier = ' upload-status-chip--error';
        icon = <ErrorFilled size={14} className="upload-status-chip__icon" />;
        label = doneCount > 0
            ? `${doneCount} uploaded · ${errorCount} failed`
            : `${errorCount} file${errorCount !== 1 ? 's' : ''} failed`;
    } else {
        chipModifier = ' upload-status-chip--done';
        icon = <CheckmarkFilled size={14} className="upload-status-chip__icon" />;
        label = `${doneCount} file${doneCount !== 1 ? 's' : ''} uploaded`;
    }

    return (
        <button
            type="button"
            className={`upload-status-chip${chipModifier}`}
            onClick={onOpen}
            aria-label={`${label} — click to view`}
        >
            {icon}
            <span className="upload-status-chip__label">{label}</span>
        </button>
    );
});

export default UploadStatusChip;
